import React from "react";
import { Box, Heading, Text, Stack, Flex } from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import LoadingSpinner from "../LoadingSpinner";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend
);

export default function GraficoCriancasAdolescentes({
  dados,
  dadosGrafico,
  loadingGraficoCrianca,
}) {
  const nomesMesCompleto = dadosGrafico.map((item) => item?.nomeMes);
  const labels = dadosGrafico.map((item, index) => {
    if (!item?.mesReferencia) return "";
    const [ano, mes] = item.mesReferencia.split("-");
    const ultimoDia = new Date(ano, mes, 0).getDate();
    const nomeMesAbrev = item.nomeMes.slice(0, 3).toUpperCase();

    const isUltimo = index === dadosGrafico.length - 1;

    return isUltimo
      ? `${nomeMesAbrev}`
      : `${String(ultimoDia).padStart(2, "0")}/${nomeMesAbrev}`;
  });

  const tiposConfig = {
    "Abrigo Institucional": {
      chave: "quantidadeInstitucional",
      label:
        "Serviço de Acolhimento Institucional (Modalidade Abrigo Institucional)",
      color: "#5689A6",
      borderColor: "#3b82f6",
      dash: [],
      fill: true,
      gradient: true,
    },
    "Não se aplica": {
      chave: "quantidadeFamiliar",
      label: "Serviço de Acolhimento Familiar",
      color: "#7ACEBA",
      borderColor: "#38b2ac",
      dash: [5, 5],
      fill: false,
    },
    "Casa Lar": {
      chave: "quantidadeCasaLar",
      label: "Serviço de Acolhimento Institucional (Modalidade Casa Lar)",
      color: "#B5CEC8",
      borderColor: "#b5cec8",
      dash: [2, 2],
      fill: false,
    },
  };

  const tiposParaExibir = dados?.tipoServicoLogado
    ? [dados.tipoServicoLogado]
    : Object.keys(tiposConfig);

  const datasets = tiposParaExibir.map((tipo) => {
    const conf = tiposConfig[tipo];
    const valores = dadosGrafico.map((item) => item?.[conf.chave] ?? 0);

    return {
      label: conf.label,
      data: valores,
      fill: conf.fill,
      borderColor: conf.color,
      borderDash: conf.dash,
      tension: 0,
      pointRadius: 4,
      pointBackgroundColor: conf.color,
      backgroundColor: conf.gradient
        ? (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, "rgba(59, 130, 246, 0.2)");
            gradient.addColorStop(1, "rgba(59, 130, 246, 0)");
            return gradient;
          }
        : conf.color,
    };
  });

  const datasetsFiltrados = datasets?.filter((d) =>
    d?.data?.some((val) => val > 0)
  );

  const valoresLegenda = tiposParaExibir.map((tipo) => {
    const conf = tiposConfig[tipo];
    const value = dados?.[conf.chave] ?? 0;
    return {
      label: conf.label,
      value,
      color: conf.color,
      percent: dados?.total ? Math.round((value / dados.total) * 100) : 0,
    };
  });

  const data = {
    labels,
    datasets: datasetsFiltrados,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10,
        },
      },
    },
  };

  return (
    <Box
      p={6}
      shadow={"md"}
      backgroundColor="#F7F8FB"
      rounded={"lg"}
      border="1px solid #E0E6ED"
    >
      {loadingGraficoCrianca ? (
        <Flex h={"100%"} alignItems={"center"} justifyContent={"center"}>
          <LoadingSpinner />
        </Flex>
      ) : (
        <>
          <Heading fontSize="lg" mb={1} color="#2D2F44">
            Crianças e Adolescentes
          </Heading>
          <Text fontSize="sm" color="gray.500" mb={4}>
            De {nomesMesCompleto[0]} a{" "}
            {nomesMesCompleto[nomesMesCompleto.length - 1]}
          </Text>

          <Box width={"100%"} height={"180px"} mb={6}>
            <Line data={data} options={options} />
          </Box>

          <Stack spacing={3}>
            {valoresLegenda.map(({ label, value, percent, color }, index) => (
              <Flex align="center" key={index}>
                <Box
                  w="14px"
                  h="14px"
                  borderRadius="md"
                  backgroundColor={color}
                  mr={3}
                />
                <Text fontSize="sm" color="gray.700">
                  {label}{" "}
                  <strong>
                    {value} ({percent}%)
                  </strong>
                </Text>
              </Flex>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
